import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal, Form, Select, Input, DatePicker, Switch, InputNumber,
  Button, Space, Row, Col, Divider, Statistic, message, Collapse
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { additionService } from '../services';
import { useProjects, useLeaders } from '../hooks';
import {
  ADDITION_REASON_MAP, AdditionReason, ExecutorType,
  type ISubcontractor, type AdditionSubcontractSettlementMethod
} from '../types';
import { SubcontractSettlementMethod } from '../../../types';
import dayjs from 'dayjs';

const { Option } = Select;

interface BatchAdditionModalProps {
  visible: boolean;
  defaultProjectId?: string;
  subcontractors: ISubcontractor[];
  onClose: () => void;
  onSuccess: () => void;
}

interface AdditionRow {
  key: string;
  title: string;
  content: string;
  category: string;
  reason?: string;
  executorType: string;
  executorName: string;
  isSubcontracted: boolean;
  subcontractorId?: string;
  subcontractorName?: string;
  subcontractSettlementMethod?: AdditionSubcontractSettlementMethod;
  estimatedAmount: number;
  plannedStartDate?: string;
  plannedEndDate?: string;
  remark: string;
  constructionType?: string;
}

const SUBCONTRACT_SETTLEMENT_METHOD_OPTIONS = [
  { label: '单价包干', value: SubcontractSettlementMethod.UNIT_PRICE_PACKAGE },
  { label: '合同包干', value: SubcontractSettlementMethod.TOTAL_PACKAGE },
];

const CATEGORY_OPTIONS = ['土建', '安装', '装修', '园林', '外墙', '其他'];

let rowKeyCounter = 0;
const genKey = () => `row_${++rowKeyCounter}_${Date.now()}`;

interface AdditionRowFormProps {
  row: AdditionRow;
  onChange: (field: string, value: any) => void;
  subcontractorOptions: { value: string; label: string }[];
  leaderOptions: { value: string; label: string }[];
  leadersLoading?: boolean;
}

const AdditionRowForm: React.FC<AdditionRowFormProps> = ({ row, onChange, subcontractorOptions, leaderOptions, leadersLoading }) => (
  <Row gutter={12}>
    <Col span={8}>
      <Form.Item label="增项名称" required style={{ marginBottom: 8 }}>
        <Input value={row.title} placeholder="增项名称"
          onChange={e => onChange('title', e.target.value)} />
      </Form.Item>
    </Col>
    <Col span={8}>
      <Form.Item label="内容" style={{ marginBottom: 8 }}>
        <Input value={row.content} placeholder="增项内容"
          onChange={e => onChange('content', e.target.value)} />
      </Form.Item>
    </Col>
    <Col span={8}>
      <Form.Item label="类别" style={{ marginBottom: 8 }}>
        <Select value={row.category} style={{ width: '100%' }}
          onChange={val => onChange('category', val)}>
          {CATEGORY_OPTIONS.map(c => <Option key={c} value={c}>{c}</Option>)}
        </Select>
      </Form.Item>
    </Col>
    <Col span={6}>
      <Form.Item label="施工类型" style={{ marginBottom: 8 }}>
        <Select
          value={row.constructionType}
          style={{ width: '100%' }}
          allowClear
          placeholder="选择"
          onChange={val => onChange('constructionType', val)}
        >
          <Option value="园林">园林</Option>
          <Option value="土建">土建</Option>
          <Option value="外墙">外墙</Option>
          <Option value="其它">其它</Option>
        </Select>
      </Form.Item>
    </Col>
    <Col span={8}>
      <Form.Item label="施工员" style={{ marginBottom: 8 }}>
        <Select
          value={row.executorName || undefined}
          style={{ width: '100%' }}
          placeholder="施工员"
          allowClear
          showSearch
          loading={leadersLoading}
          filterOption={(input, option) =>
            (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onChange={val => onChange('executorName', val)}
          options={leaderOptions}
        />
      </Form.Item>
    </Col>
    <Col span={8}>
      <Form.Item label="增项金额" style={{ marginBottom: 8 }}>
        <InputNumber value={row.estimatedAmount} min={0} precision={2}
          style={{ width: '100%' }} prefix="¥"
          onChange={val => onChange('estimatedAmount', val || 0)} />
      </Form.Item>
    </Col>
    <Col span={8}>
      <Form.Item label="备注" style={{ marginBottom: 8 }}>
        <Input value={row.remark} placeholder="备注"
          onChange={e => onChange('remark', e.target.value)} />
      </Form.Item>
    </Col>
    <Col span={6}>
      <Form.Item label="分包" style={{ marginBottom: 8 }}>
        <Switch checked={row.isSubcontracted}
          onChange={val => onChange('isSubcontracted', val)} />
      </Form.Item>
    </Col>
    <Col span={9}>
      <Form.Item label="分包商" style={{ marginBottom: 8 }}>
        <Select
          value={row.subcontractorId}
          style={{ width: '100%' }}
          allowClear
          disabled={!row.isSubcontracted}
          placeholder="选择分包商"
          showSearch
          optionFilterProp="label"
          options={subcontractorOptions}
          onChange={(val, opt: any) => {
            onChange('subcontractorId', val);
            onChange('subcontractorName', (opt as any)?.label || '');
          }}
        />
      </Form.Item>
    </Col>
    <Col span={9}>
      <Form.Item label="分包计算方式" style={{ marginBottom: 8 }}>
        <Select
          value={row.subcontractSettlementMethod}
          style={{ width: '100%' }}
          disabled={!row.isSubcontracted}
          placeholder="选择计算方式"
          onChange={val => onChange('subcontractSettlementMethod', val)}
          options={SUBCONTRACT_SETTLEMENT_METHOD_OPTIONS}
        />
      </Form.Item>
    </Col>
  </Row>
);

const BatchAdditionModal: React.FC<BatchAdditionModalProps> = ({
  visible, defaultProjectId, subcontractors, onClose, onSuccess
}) => {
  const { projects } = useProjects();
  const { leaders, loading: leadersLoading } = useLeaders();
  const [form] = Form.useForm();
  const [rows, setRows] = useState<AdditionRow[]>([]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        projectId: defaultProjectId,
        entryDate: dayjs(),
        sourceType: AdditionReason.SITE_ADDITION,
      });
      const firstRow = createEmptyRow();
      setRows([firstRow]);
      setActiveKey(firstRow.key);
    }
  }, [visible, defaultProjectId, form]);

  const createEmptyRow = (): AdditionRow => ({
    key: genKey(),
    title: '',
    content: '',
    category: '其他',
    executorType: ExecutorType.SELF,
    executorName: '',
    isSubcontracted: false,
    estimatedAmount: 0,
    remark: '',
    constructionType: undefined,
  });

  const addRow = () => {
    if (activeKey) {
      const activeRow = rows.find(r => r.key === activeKey);
      if (activeRow && !activeRow.title.trim()) {
        message.error('请先填写当前增项的名称后再新增');
        return;
      }
      if (activeRow?.isSubcontracted && (!activeRow.subcontractorId || !activeRow.subcontractorSettlementMethod)) {
        message.error('请先完善当前增项的分包信息（分包商和计算方式）后再新增');
        return;
      }
    }
    const newRow = createEmptyRow();
    setRows(prev => [...prev, newRow]);
    setActiveKey(newRow.key);
  };

  const removeRow = (key: string) => {
    if (rows.length <= 1) { message.warning('至少保留一条增项'); return; }
    const willBeActive = activeKey === key;
    setRows(prev => prev.filter(r => r.key !== key));
    if (willBeActive) {
      setActiveKey(null);
    }
  };

  const updateRow = (key: string, field: string, value: any) => {
    setRows(prev => prev.map(r => {
      if (r.key !== key) return r;
      const updated = { ...r, [field]: value };
      if (field === 'isSubcontracted' && !value) {
        updated.subcontractorId = undefined;
        updated.subcontractorName = undefined;
        updated.subcontractSettlementMethod = undefined;
        updated.executorType = ExecutorType.SELF;
      }
      if (field === 'isSubcontracted' && value) {
        updated.executorType = ExecutorType.SUBCONTRACT;
      }
      return updated;
    }));
  };

  const totalAmount = rows.reduce((sum, r) => sum + (r.estimatedAmount || 0), 0);
  const subCount = rows.filter(r => r.isSubcontracted).length;

  const subcontractorOptions = useMemo(
    () => subcontractors.map(s => ({ value: s._id, label: s.name })),
    [subcontractors]
  );

  const leaderOptions = useMemo(
    () => leaders.map(l => ({ value: l.name, label: l.name })),
    [leaders]
  );

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const emptyTitles = rows.filter(r => !r.title.trim());
      if (emptyTitles.length > 0) {
        message.error('请填写所有增项的名称');
        return;
      }

      const invalidSubcontractRows = rows.filter(r => r.isSubcontracted && (!r.subcontractorId || !r.subcontractSettlementMethod));
      if (invalidSubcontractRows.length > 0) {
        message.error('打开分包后，必须逐行选择分包商和分包计算方式');
        return;
      }

      setSubmitting(true);
      const response = await additionService.batchCreate({
        projectId: values.projectId,
        entryDate: values.entryDate?.format('YYYY-MM-DD'),
        sourceType: values.sourceType,
        sourceDescription: values.sourceDescription,
        location: values.location,
        items: rows.map(r => ({
          title: r.title,
          content: r.content,
          category: r.category,
          reason: r.reason || values.sourceType,
          executorType: r.executorType,
          executorName: r.executorName,
          isSubcontracted: r.isSubcontracted,
          subcontractorId: r.subcontractorId,
          subcontractorName: r.subcontractorName,
          subcontractSettlementMethod: r.subcontractSettlementMethod,
          estimatedAmount: r.estimatedAmount,
          plannedStartDate: r.plannedStartDate,
          plannedEndDate: r.plannedEndDate,
          remark: r.remark,
          constructionType: r.constructionType,
        }))
      });

      if (response.success) {
        message.success(response.message || '批量创建成功');
        form.resetFields();
        setRows([]);
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      if (error.errorFields) return;
      message.error(error.message || '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCollapseChange = (keys: string | string[]) => {
    if (Array.isArray(keys)) {
      setActiveKey(keys.length > 0 ? keys[0] : null);
    } else {
      setActiveKey(keys || null);
    }
  };

  return (
    <Modal
      title="批量录入增项"
      open={visible}
      onCancel={onClose}
      width={1200}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>取消</Button>,
        <Button key="submit" type="primary" loading={submitting} onClick={handleSubmit}>
          确认提交（{rows.length} 条）
        </Button>
      ]}
    >
      <Form form={form} layout="vertical" size="small">
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name="projectId" label="所属项目" rules={[{ required: true, message: '请选择项目' }]}>
              <Select placeholder="选择项目" showSearch optionFilterProp="children">
                {projects.map(p => <Option key={p._id} value={p._id}>{p.name}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="entryDate" label="增项确认时间" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="sourceType" label="来源类型" rules={[{ required: true }]}>
              <Select>
                {Object.entries(ADDITION_REASON_MAP).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item name="location" label="现场位置">
              <Input placeholder="如：B2东侧机房" />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item name="sourceDescription" label="来源说明">
              <Input placeholder="来源说明" />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Divider style={{ margin: '8px 0' }} />

      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Button size="small" icon={<PlusOutlined />} onClick={addRow}>新增一条</Button>
        </Space>
        <Space size="large">
          <Statistic title="总条数" value={rows.length} suffix="条" valueStyle={{ fontSize: 16 }} />
          <Statistic title="总金额" value={totalAmount} precision={2} prefix="¥" valueStyle={{ fontSize: 16 }} />
          <Statistic title="分包" value={subCount} suffix="条" valueStyle={{ fontSize: 16 }} />
        </Space>
      </div>

      <Collapse
        activeKey={activeKey}
        onChange={handleCollapseChange}
        accordion
        style={{ maxHeight: 400, overflowY: 'auto' }}
      >
        {rows.map((row, idx) => (
          <Collapse.Panel
            key={row.key}
            header={
              <Space>
                <span>增项 #{idx + 1}</span>
                <span style={{ fontWeight: 500 }}>
                  {row.title || <span style={{ color: '#999' }}>未填写名称</span>}
                </span>
              </Space>
            }
            extra={
              <Button type="text" danger size="small" icon={<DeleteOutlined />}
                onClick={(e) => { e.stopPropagation(); removeRow(row.key); }} />
            }
          >
            <AdditionRowForm
              row={row}
              onChange={(field, value) => updateRow(row.key, field, value)}
              subcontractorOptions={subcontractorOptions}
              leaderOptions={leaderOptions}
              leadersLoading={leadersLoading}
            />
          </Collapse.Panel>
        ))}
      </Collapse>
    </Modal>
  );
};

export default BatchAdditionModal;
